import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { ExternalLink, Smartphone, Code2, Accessibility, Zap, MessageCircle, Award, Hammer, TreeDeciduous, Plane, Globe, Database, TrendingUp, Laptop, Rocket, Users, Eye, Brain, Heart } from 'lucide-react';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { PrimaryCTA } from '@/components/PrimaryCTA';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import scottWallerLogo from '@/assets/scott-waller-logo.avif';
import moreAbilityLogo from '@/assets/more-ability-logo.avif';
import allInclusiveHolsLogo from '@/assets/all-inclusive-hols-logo.avif';
import bridgendTechHelpLogo from '@/assets/bridgend-tech-help-logo.png';
import roofingImage from '@/assets/roofing-laptop.avif';
import landscapingImage from '@/assets/landscaping-monitor.avif';
import travelImage from '@/assets/travel-tablet.avif';

// SEO Meta
const pageTitle = "Portfolio | Trade Websites - SmartSite Solutions";
const pageDescription = "See our high-performance websites for plumbers, roofers and builders in Cardiff, Swansea, Newport and Bridgend. 90+ PageSpeed scores that generate real leads.";
export default function Portfolio() {
  // Set page title and meta
  useEffect(() => {
    // Add schema
    const schema = {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "name": "SmartSite Solutions Portfolio",
      "description": pageDescription,
      "publisher": {
        "@type": "Organization",
        "name": "SmartSite Solutions"
      }
    };
    let scriptTag = document.querySelector('script[data-schema="portfolio"]');
    if (!scriptTag) {
      scriptTag = document.createElement('script');
      scriptTag.setAttribute('type', 'application/ld+json');
      scriptTag.setAttribute('data-schema', 'portfolio');
      document.head.appendChild(scriptTag);
    }
    scriptTag.textContent = JSON.stringify(schema);
    return () => {
      if (scriptTag) {
        scriptTag.remove();
      }
    };
  }, []);
  const breadcrumbItems = [{
    label: 'Home',
    href: '/'
  }, {
    label: 'Portfolio',
    href: '/portfolio'
  }];
  const heroMetrics = [{
    value: '100',
    label: 'Mobile Score',
    sublabel: 'Google PageSpeed',
    icon: Smartphone
  }, {
    value: 'React',
    label: 'Architecture',
    sublabel: 'Next Gen Tech',
    icon: Code2
  }, {
    value: 'WCAG',
    label: 'Compliant',
    sublabel: 'Accessibility Standard',
    icon: Accessibility
  }];
  const moreAbilityFeatures = [{
    title: 'Custom Accessibility Engine:',
    description: 'Built-in text resizing & contrast tools.',
    icon: Eye
  }, {
    title: 'AI-Ready Schema:',
    description: 'Advanced markup ensures the site is understood and recommended by AI tools like ChatGPT & Gemini.',
    icon: Brain
  }, {
    title: 'Trust-First Design:',
    description: 'Clean, calming UI that builds instant credibility.',
    icon: Heart
  }];
  const scottWallerResults = [{
    title: 'Instant Load Times:',
    description: '0.5s load speed on 4G networks.',
    icon: Zap
  }, {
    title: 'One-Touch Action Tech:',
    description: 'Sticky mobile buttons for one-tap calling and WhatsApp messaging. No copying numbers required.',
    icon: MessageCircle
  }, {
    title: 'Premium Brand Feel:',
    description: 'A site that justifies premium pricing.',
    icon: Award
  }];
  const allInclusiveHolsFeatures = [{
    title: 'Research-Driven Design:',
    description: 'Full market analysis to solve real customer pain points.',
    icon: TrendingUp
  }, {
    title: 'Full Admin Backend:',
    description: 'Database-driven platform allowing the founder to manage deals in real-time.',
    icon: Database
  }, {
    title: 'Outperforms Market Leaders:',
    description: 'Faster and more user-friendly than major travel booking sites.',
    icon: Globe
  }];
  const bridgendTechHelpFeatures = [{
    title: 'Built to Convert:',
    description: 'Clean landing page engineered for lead generation.',
    icon: Rocket
  }, {
    title: 'Scalable Foundation:',
    description: 'Designed for growth with room for pages, tools and features.',
    icon: Laptop
  }, {
    title: 'Long-Term Partnership:',
    description: 'Real relationships, not just a one-off build.',
    icon: Users
  }];
  const conceptBuilds = [{
    title: 'The Roofer Scale-Up',
    industry: 'Roofing',
    badge: 'Available',
    badgeColor: 'bg-emerald-500/20 text-emerald-400',
    description: 'Engineered for emergency capture. "Click-to-Call" prioritised.',
    icon: Hammer,
    gradient: 'from-orange-500/20 to-red-500/20',
    image: roofingImage
  }, {
    title: 'The Landscape Lead-Gen',
    industry: 'Landscaping',
    badge: 'Available',
    badgeColor: 'bg-emerald-500/20 text-emerald-400',
    description: 'Visual-heavy framework designed to showcase portfolio work.',
    icon: TreeDeciduous,
    gradient: 'from-green-500/20 to-emerald-500/20',
    image: landscapingImage
  }, {
    title: 'Project: Horizon',
    industry: 'Travel',
    badge: 'In Development',
    badgeColor: 'bg-amber-500/20 text-amber-400',
    image: travelImage,
    description: 'A high-speed booking engine for a bespoke travel consultancy.',
    icon: Plane,
    gradient: 'from-blue-500/20 to-cyan-500/20'
  }];
  return <div className="min-h-screen bg-background">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href="https://smartsite-lead-gen.lovable.app/portfolio" />
      </Helmet>
      <Navbar />
      
      <main className="pt-32 pb-24">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumbs */}
          <Breadcrumbs items={breadcrumbItems} />

          {/* Section 1: Hero */}
          <motion.section initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-center max-w-4xl mx-auto mb-20">
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              We Don't Just Build Websites.{' '}
              <span className="text-gradient">We Build Business Assets.</span>
            </h1>
            <p className="text-lg lg:text-xl text-muted-foreground mb-12 max-w-3xl mx-auto">
              Speed, Accessibility, and Conversion. We engineer digital platforms that set the standard for local trades.
            </p>

            {/* Metrics Row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 max-w-3xl mx-auto">
              {heroMetrics.map((metric, index) => <motion.div key={metric.label} initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.1 + index * 0.1
            }} className="glass-card p-6 text-center">
                  <metric.icon className="w-8 h-8 mx-auto mb-3 text-cyan-400" />
                  <div className="text-2xl font-display font-bold text-white mb-1">{metric.value}</div>
                  <div className="text-sm text-muted-foreground">{metric.label}</div>
                  <div className="text-xs text-slate-500">{metric.sublabel}</div>
                </motion.div>)}
            </div>
          </motion.section>

          {/* Section 2: Flagship - All Inclusive Hols */}
          <motion.section initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="mb-20">
            <div className="relative rounded-3xl overflow-hidden bg-slate-950/50 border border-emerald-500/30 shadow-[0_0_60px_rgba(16,185,129,0.15)]">
              {/* Emerald Glow Effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/10 via-transparent to-teal-500/5 pointer-events-none" />
              
              <div className="relative p-6 md:p-8 lg:p-12">
                {/* Hero Logo - Centred Prominently */}
                <div className="flex flex-col items-center text-center mb-8">
                  <span className="inline-block px-4 py-1.5 rounded-full bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 text-sm font-medium mb-6">
                    Flagship Project
                  </span>
                  <div className="w-36 h-36 md:w-44 md:h-44 mb-4">
                    <img src={allInclusiveHolsLogo} alt="All Inclusive Hols logo" loading="eager" fetchPriority="high" decoding="async" className="w-full h-full object-contain drop-shadow-[0_0_40px_rgba(16,185,129,0.6)]" />
                  </div>
                  <p className="text-sm text-emerald-400/80 font-medium tracking-wide uppercase">
                    All-Inclusive Travel Deals
                  </p>
                </div>

                {/* Text Content */}
                <div>
                  <h2 className="font-display text-3xl lg:text-4xl font-bold mb-6 text-center lg:text-left">
                    All Inclusive Hols:{' '}
                    <span className="text-emerald-400">
                      Built to Dominate
                    </span>
                  </h2>
                  
                  <div className="grid lg:grid-cols-2 gap-6 mb-8">
                    <div>
                      <h3 className="text-sm font-semibold text-emerald-400 uppercase tracking-wider mb-2">The Challenge</h3>
                      <p className="text-muted-foreground">
                        The travel market is dominated by slow, bloated booking sites. We needed to build a platform that outperformed them all whilst solving the real pain points customers face.
                      </p>
                    </div>
                    
                    <div>
                      <h3 className="text-sm font-semibold text-emerald-400 uppercase tracking-wider mb-2">The SmartSite Solution</h3>
                      <p className="text-muted-foreground">
                        Full market research drove every decision. The result: a lightning-fast, database-driven platform with a complete admin backend for real-time deal management.
                      </p>
                    </div>
                  </div>

                  {/* Features - 3 columns on desktop */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 mb-8">
                    {allInclusiveHolsFeatures.map(feature => <div key={feature.title} className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                          <feature.icon className="w-4 h-4 text-emerald-400" />
                        </div>
                        <div>
                          <span className="text-white font-medium text-sm">{feature.title}</span>{' '}
                          <span className="text-muted-foreground text-sm">{feature.description}</span>
                        </div>
                      </div>)}
                  </div>

                  <a href="https://www.allinclusivehols.co.uk/" target="_blank" rel="noopener noreferrer" className="block">
                    <Button className="bg-emerald-500 hover:bg-emerald-600 text-white shadow-lg shadow-emerald-500/25 w-full lg:w-auto">
                      <ExternalLink className="w-4 h-4 mr-2" />
                      View Live Website
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </motion.section>

          {/* Section 3: Crown Jewel - More Ability Bathing */}
          <motion.section initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="mb-20">
            <div className="relative rounded-3xl overflow-hidden bg-slate-950/50 border border-amber-500/30 shadow-[0_0_60px_rgba(245,158,11,0.15)]">
              {/* Gold Glow Effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 via-transparent to-amber-500/5 pointer-events-none" />
              
              <div className="relative p-6 md:p-8 lg:p-12">
                {/* Hero Logo - Centred Prominently */}
                <div className="flex flex-col items-center text-center mb-8">
                  <span className="inline-block px-4 py-1.5 rounded-full bg-amber-500/20 border border-amber-500/30 text-amber-400 text-sm font-medium mb-6">
                    Featured Project
                  </span>
                  <div className="w-36 h-36 md:w-44 md:h-44 mb-4">
                    <img src={moreAbilityLogo} alt="More Ability Bathing logo" loading="eager" fetchPriority="high" decoding="async" className="w-full h-full object-contain drop-shadow-[0_0_40px_rgba(245,158,11,0.6)]" />
                  </div>
                  <p className="text-sm text-amber-400/80 font-medium tracking-wide uppercase">
                    Accessibility Specialists
                  </p>
                </div>

                {/* Text Content */}
                <div>
                  <h2 className="font-display text-3xl lg:text-4xl font-bold mb-6 text-center lg:text-left">
                    More Ability Bathing:{' '}
                    <span className="text-amber-400">
                      Engineering Empathy
                    </span>
                  </h2>
                  
                  <div className="grid lg:grid-cols-2 gap-6 mb-8">
                    <div>
                      <h3 className="text-sm font-semibold text-amber-400 uppercase tracking-wider mb-2">The Challenge</h3>
                      <p className="text-muted-foreground">
                        Selling accessible bathrooms requires trust. The site needed to be instantly usable for elderly clients and those with visual impairments.
                      </p>
                    </div>
                    
                    <div>
                      <h3 className="text-sm font-semibold text-amber-400 uppercase tracking-wider mb-2">The SmartSite Solution</h3>
                      <p className="text-muted-foreground">
                        We moved beyond standard templates to build a fully accessible digital showroom.
                      </p>
                    </div>
                  </div>

                  {/* Features - 3 columns on desktop */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 mb-8">
                    {moreAbilityFeatures.map(feature => <div key={feature.title} className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center">
                          <feature.icon className="w-4 h-4 text-amber-400" />
                        </div>
                        <div>
                          <span className="text-white font-medium text-sm">{feature.title}</span>{' '}
                          <span className="text-muted-foreground text-sm">{feature.description}</span>
                        </div>
                      </div>)}
                  </div>

                  <a href="https://www.moreabilitybathing.co.uk/" target="_blank" rel="noopener noreferrer" className="block">
                    <Button className="bg-amber-500 hover:bg-amber-600 text-white shadow-lg shadow-amber-500/25 w-full lg:w-auto">
                      <ExternalLink className="w-4 h-4 mr-2" />
                      View Live Website
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </motion.section>

          {/* Section 4: Bridgend Tech Help */}
          <motion.section initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="mb-20">
            <div className="relative rounded-3xl overflow-hidden bg-slate-950/50 border border-red-500/30 shadow-[0_0_60px_rgba(239,68,68,0.15)]">
              {/* Red Glow Effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-red-500/10 via-transparent to-orange-500/5 pointer-events-none" />
              
              <div className="relative p-6 md:p-8 lg:p-12">
                {/* Hero Logo - Centred Prominently */}
                <div className="flex flex-col items-center text-center mb-8">
                  <span className="inline-block px-4 py-1.5 rounded-full bg-red-500/20 border border-red-500/30 text-red-400 text-sm font-medium mb-6">
                    Growth Partner
                  </span>
                  <div className="w-36 h-36 md:w-44 md:h-44 mb-4">
                    <img src={bridgendTechHelpLogo} alt="Bridgend Tech Help logo" loading="lazy" decoding="async" className="w-full h-full object-contain drop-shadow-[0_0_40px_rgba(239,68,68,0.6)]" />
                  </div>
                  <p className="text-sm text-red-400/80 font-medium tracking-wide uppercase">
                    Local Tech Support
                  </p>
                </div>

                {/* Text Content */}
                <div>
                  <h2 className="font-display text-3xl lg:text-4xl font-bold mb-6 text-center lg:text-left">
                    Bridgend Tech Help:{' '}
                    <span className="text-red-400">
                      Built for Growth
                    </span>
                  </h2>
                  
                  <div className="grid lg:grid-cols-2 gap-6 mb-8">
                    <div>
                      <h3 className="text-sm font-semibold text-red-400 uppercase tracking-wider mb-2">The Challenge</h3>
                      <p className="text-muted-foreground">
                        A new local tech support business needed a professional online presence that could grow alongside them.
                      </p>
                    </div>
                    
                    <div>
                      <h3 className="text-sm font-semibold text-red-400 uppercase tracking-wider mb-2">The SmartSite Solution</h3>
                      <p className="text-muted-foreground">
                        We built a conversion-focused landing page with a scalable architecture ready for new pages, tools and features as the business expands.
                      </p>
                    </div>
                  </div>

                  {/* Features - 3 columns on desktop */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 mb-8">
                    {bridgendTechHelpFeatures.map(feature => <div key={feature.title} className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-red-500/20 flex items-center justify-center">
                          <feature.icon className="w-4 h-4 text-red-400" />
                        </div>
                        <div>
                          <span className="text-white font-medium text-sm">{feature.title}</span>{' '}
                          <span className="text-muted-foreground text-sm">{feature.description}</span>
                        </div>
                      </div>)}
                  </div>

                  <a href="https://bridgendtechhelp.co.uk" target="_blank" rel="noopener noreferrer" className="block">
                    <Button className="bg-red-500 hover:bg-red-600 text-white shadow-lg shadow-red-500/25 w-full lg:w-auto">
                      <ExternalLink className="w-4 h-4 mr-2" />
                      View Live Website
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </motion.section>

          {/* Section 5: Local Hero - Scott Waller Plumbing */}
          <motion.section initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="mb-20">
            <div className="relative rounded-3xl overflow-hidden bg-white/5 backdrop-blur-xl border border-cyan-500/30 shadow-[0_0_60px_rgba(34,211,238,0.1)]">
              {/* Cyan Glow Effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 via-transparent to-blue-500/5 pointer-events-none" />
              
              <div className="relative p-6 md:p-8 lg:p-12">
                {/* Hero Logo - Centred Prominently */}
                <div className="flex flex-col items-center text-center mb-8">
                  <span className="inline-block px-4 py-1.5 rounded-full bg-cyan-500/20 border border-cyan-500/30 text-cyan-400 text-sm font-medium mb-6">
                    Case Study
                  </span>
                  <div className="w-36 h-36 md:w-44 md:h-44 mb-4">
                    <img src={scottWallerLogo} alt="Scott Waller Plumbing & Heating logo" loading="lazy" decoding="async" className="w-full h-full object-contain drop-shadow-[0_0_40px_rgba(34,211,238,0.6)]" />
                  </div>
                  <p className="text-sm text-cyan-400/80 font-medium tracking-wide uppercase">
                    Plumbing &amp; Heating
                  </p>
                </div>

                {/* Text Content */}
                <div>
                  <h2 className="font-display text-3xl lg:text-4xl font-bold mb-6 text-center lg:text-left">
                    Scott Waller:{' '}
                    <span className="text-cyan-400">
                      The Professional Upgrade
                    </span>
                  </h2>
                  
                  <div className="grid lg:grid-cols-2 gap-6 mb-8">
                    <div>
                      <h3 className="text-sm font-semibold text-cyan-400 uppercase tracking-wider mb-2">The Challenge</h3>
                      <p className="text-muted-foreground">
                        A top-rated plumber relying on word-of-mouth needed a digital presence that matched his 5-star reputation.
                      </p>
                    </div>
                    
                    <div>
                      <h3 className="text-sm font-semibold text-cyan-400 uppercase tracking-wider mb-2">The SmartSite Solution</h3>
                      <p className="text-muted-foreground">
                        We replaced the "DIY" look with a high-speed, professional platform.
                      </p>
                    </div>
                  </div>

                  {/* Results - 3 columns on desktop */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 mb-8">
                    {scottWallerResults.map(result => <div key={result.title} className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-cyan-500/20 flex items-center justify-center">
                          <result.icon className="w-4 h-4 text-cyan-400" />
                        </div>
                        <div>
                          <span className="text-white font-medium text-sm">{result.title}</span>{' '}
                          <span className="text-muted-foreground text-sm">{result.description}</span>
                        </div>
                      </div>)}
                  </div>

                  <a href="https://www.scottwallerplumbing.co.uk/" target="_blank" rel="noopener noreferrer" className="block">
                    <Button className="bg-cyan-500 hover:bg-cyan-600 text-white shadow-lg shadow-cyan-500/25 w-full lg:w-auto">
                      <ExternalLink className="w-4 h-4 mr-2" />
                      View Live Website
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </motion.section>

          {/* Section 5: SmartSite Lab */}
          <motion.section initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="mb-20">
            <div className="text-center mb-12">
              <h2 className="font-display text-3xl lg:text-4xl font-bold mb-4">
                Ready-to-Deploy <span className="text-gradient">Engines</span>
              </h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                We have pre-engineered high-performance frameworks for these industries. Skip the design phase—launch in 7 days.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
              {conceptBuilds.map((build, index) => <motion.div key={build.title} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.1
            }} className="glass-card overflow-hidden group hover:border-white/20 transition-all duration-300">
                  {/* Image Area */}
                  <div className="relative h-56 w-full overflow-hidden rounded-t-2xl">
                    <img src={build.image} alt={`${build.title} - ${build.industry} website mockup`} loading="lazy" decoding="async" sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw" className="w-full h-full object-cover" />
                    {/* Badge */}
                    <div className="absolute top-3 right-3">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium backdrop-blur-sm ${build.badgeColor}`}>
                        {build.badge}
                      </span>
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="p-6">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">{build.industry}</p>
                    <h3 className="font-display text-xl font-bold mb-3 group-hover:text-gradient transition-all duration-300">
                      {build.title}
                    </h3>
                    <p className="text-sm text-muted-foreground">{build.description}</p>
                  </div>
                </motion.div>)}
            </div>
          </motion.section>

          {/* Section 5: Final CTA */}
          <motion.section initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center">
            <div className="glass-card p-12 max-w-2xl mx-auto text-center">
              <h2 className="font-display text-2xl lg:text-3xl font-bold mb-6">
                See the difference speed makes.
              </h2>
              <PrimaryCTA 
                text="Get Your Free Audit" 
                href="/contact" 
                external={false}
                size="lg"
              />
            </div>
          </motion.section>
        </div>
      </main>

      <Footer />
    </div>;
}